import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Toast} from "antd-mobile";
import NFTModal from "@/components/NFTModal";
import {hideLoading, showLoading} from "@/components/loading";
import Service from "../service";
import {ACCESSORY_TABS, MOCK_COLLECTION_LIST, TYPE_TABS} from "./mock";
import Styles from "./index.module.scss";

const USE_MOCK = Service.mock;

/** 将接口 MaterialItemResp 转为页面使用的素材结构 */
const normalizeMaterial = (item = {}) => {
  const type = Number(item.type);
  const cate = Number(item.cate);
  const goodsId = item.goodsId;
  const count = Number(item.count) || 0;
  const name = item.name || '';
  const imageUrl = item.imageUrl || '';

  return {
    ...item,
    goodsId,
    id: goodsId,
    nftNo: String(goodsId),
    collectionName: name,
    nftImage: imageUrl,
    itemType: type === 2 ? 'accessory' : 'role',
    roleType: type === 1 ? cate : undefined,
    accessoryType: type === 2 ? cate : undefined,
    ownedCount: count,
  };
};

/**
 * @description 合成（话剧）
 * @return {/culturalExchange_huaju/exchangepage}
 */

const ExchangePage = () => {
  const navigate = useNavigate();
  const [roleList, setRoleList] = useState([]);
  const [accessoryList, setAccessoryList] = useState([]);
  const [step, setStep] = useState(1); // 1 选角色 2 选配件
  const [activeType, setActiveType] = useState(TYPE_TABS[0].cate);
  const [activeAccessoryType, setActiveAccessoryType] = useState(ACCESSORY_TABS[0].cate);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const [statusBarHeight, setStatusBarHeight] = useState(0);
  const [roleInitialized, setRoleInitialized] = useState(false);

  const typeTabs = useMemo(() => TYPE_TABS, []);
  const accessoryTabs = useMemo(() => ACCESSORY_TABS, []);

  useEffect(() => {
    if (window.JsBridge?.hasWebViewBridge?.()) {
      window.JsBridge.setStatusBar?.('setBarStyle', ['dark-content']);
      window.JsBridge.getStatusBarHeight().then(({height}) => {
        setStatusBarHeight(height || 0);
      });
    }
    getRoleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (roleInitialized || !roleList.length) return;
    const firstRole = roleList[0];
    if (firstRole) {
      setSelectedRole(firstRole);
      setActiveType(firstRole.roleType ?? TYPE_TABS[0].cate);
    }
    setRoleInitialized(true);
  }, [roleList, roleInitialized]);

  const getRoleData = async () => {
    if (USE_MOCK) {
      setRoleList(MOCK_COLLECTION_LIST.filter((item) => item.itemType === 'role').map(normalizeMaterial));
      return;
    }
    try {
      showLoading();
      const res = await Service.queryMaterials({ type: 1 });
      if (res.code !== '000000') throw new Error(res.msg || '加载失败');
      const list = (res.data || []).map(normalizeMaterial);
      setRoleList(list);
    } catch (e) {
      Toast.show(e?.message || '加载失败');
      setRoleList([]);
    } finally {
      hideLoading();
    }
  };

  const getAccessoryData = async (roleId) => {
    if (USE_MOCK) {
      setAccessoryList(
        MOCK_COLLECTION_LIST.filter((item) => item.itemType === 'accessory').map(normalizeMaterial),
      );
      return;
    }
    try {
      showLoading();
      // 不选角色也可查：返回持有的专属配件 + 通用配件；有选中角色时带上 selectedRoleId
      const params = { type: 2 };
      if (roleId != null && roleId !== '') {
        params.selectedRoleId = roleId;
      }
      const res = await Service.queryMaterials(params);
      if (res.code !== '000000') throw new Error(res.msg || '加载失败');
      setAccessoryList((res.data || []).map(normalizeMaterial));
    } catch (e) {
      Toast.show(e?.message || '加载失败');
      setAccessoryList([]);
    } finally {
      hideLoading();
    }
  };

  const handleBack = () => {
    if (window.JsBridge?.hasWebViewBridge?.()) {
      window.JsBridge.back();
    } else {
      navigate(-1);
    }
  };

  const filteredList = useMemo(() => {
    if (step === 2) {
      return accessoryList.filter((item) => item.accessoryType === activeAccessoryType);
    }
    return roleList.filter((item) => item.roleType === activeType);
  }, [roleList, accessoryList, step, activeType, activeAccessoryType]);

  // 配件按 cate 叠层：衣服 → 面具 → 饰品（对齐孵蛋多层绝对定位叠加）
  const previewAccessoryLayers = useMemo(
    () =>
      [...selectedAccessories]
        .filter((item) => item?.nftImage)
        .sort((a, b) => (a.accessoryType ?? 0) - (b.accessoryType ?? 0)),
    [selectedAccessories],
  );

  const getItemKey = (item) => item?.goodsId ?? item?.nftNo;

  const onSelectRole = (item) => {
    if (getItemKey(selectedRole) === getItemKey(item)) {
      setSelectedRole(null);
    } else {
      setSelectedRole(item);
      setSelectedAccessories([]);
    }
  };

  const onToggleAccessory = (item) => {
    const key = getItemKey(item);
    const exists = selectedAccessories.some((x) => getItemKey(x) === key);
    if (exists) {
      // 再次点击取消选中
      setSelectedAccessories(selectedAccessories.filter((x) => getItemKey(x) !== key));
      return;
    }
    // 同类型（衣服/面具/饰品）只能选 1 个：选新的会替换旧的；不同类型可同时多选
    const type = Number(item.accessoryType ?? activeAccessoryType);
    const next = selectedAccessories.filter(
      (x) => Number(x.accessoryType) !== type,
    );
    setSelectedAccessories([...next, item]);
  };

  const onNext = async () => {
    // 不选角色也可进入配件页，便于查看持有的专属/通用配件
    setStep(2);
    setActiveAccessoryType(ACCESSORY_TABS[0].cate);
    setSelectedAccessories([]);
    await getAccessoryData(selectedRole?.goodsId);
  };

  const onLast = () => {
    setStep(1);
    setSelectedAccessories([]);
  };

  const onCompound = () => {
    if (!selectedRole) return Toast.show('请选择角色');
    // App 内合成前先校验实名
    if (window.JsBridge?.hasWebViewBridge?.()) {
      window.JsBridge.verifyRealName('请先实名认证').then((res) => {
        if (res?.result) {
          setConfirmVisible(true);
        }
      });
      return;
    }
    setConfirmVisible(true);
  };

  const onConfirmCompound = async () => {
    if (!selectedRole?.goodsId) return Toast.show('请选择角色');
    const params = {
      roleGoodsId: selectedRole.goodsId,
      accessoryGoodsIds: selectedAccessories.map((item) => item.goodsId).filter(Boolean),
    };
    if (USE_MOCK) {
      setConfirmVisible(false);
      Toast.show('合成成功');
      const resultType = selectedRole.roleType ?? selectedRole.cate;
      navigate(`/culturalExchange_huaju/exchangeRecord${resultType ? `?resultType=${resultType}` : ''}`);
      return;
    }
    try {
      showLoading();
      const res = await Service.synthesize(params);
      if (res.code !== '000000') throw new Error(res.msg || '合成失败');
      setConfirmVisible(false);
      const status = Number(res.data?.status);
      if (status === 0) {
        Toast.show('合成成功，请先完成实名认证后发放');
      } else {
        Toast.show('合成成功');
      }
      const resultType = selectedRole.roleType ?? selectedRole.cate;
      navigate(`/culturalExchange_huaju/exchangeRecord${resultType ? `?resultType=${resultType}` : ''}`);
    } catch (e) {
      Toast.show(e?.message || '合成失败');
    } finally {
      hideLoading();
    }
  };

  return (
    <div className={Styles.huajuComposePage}>
      <div className={`${Styles.previewSection} ${selectedRole ? '' : Styles.previewSectionEmpty}`}>
        {selectedRole ? (
          <>
            <img
              className={Styles.previewCharacter}
              src={selectedRole.nftImage}
              alt={selectedRole.collectionName || selectedRole.nftNo}
            />
            {previewAccessoryLayers.map((item) => (
              <img
                key={getItemKey(item)}
                className={Styles.previewAccessory}
                src={item.nftImage}
                alt={item.collectionName || item.nftNo}
              />
            ))}
          </>
        ) : null}
        <div className={Styles.previewOverlay}>
          <div
            className={Styles.statusBar}
            style={statusBarHeight ? {height: statusBarHeight} : undefined}
          />
          <div className={Styles.navBar}>
            <div
              className={Styles.backButton}
              onClick={handleBack}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleBack()}
              aria-label="返回"
            />
          </div>
        </div>
      </div>

      <div className={Styles.selectionPanel}>
        <div className={`${Styles.typeTabs} ${step === 2 ? Styles.typeTabsAccessory : ''}`}>
          {(step === 1 ? typeTabs : accessoryTabs).map((t) => {
            const isActive = step === 1 ? activeType === t.cate : activeAccessoryType === t.cate;
            return (
              <button
                key={`${step}-${t.cate}`}
                type="button"
                className={`${Styles.typeTab} ${isActive ? Styles.typeTabActive : Styles.typeTabInactive}`}
                onClick={() => (step === 1 ? setActiveType(t.cate) : setActiveAccessoryType(t.cate))}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className={Styles.gridScrollArea}>
          {filteredList.length === 0 ? (
            <div className={Styles.noData}>
              <div className={Styles.noDataText}>当前无可合成素材</div>
            </div>
          ) : (
            <div className={Styles.grid}>
              {filteredList.map((item) => {
                const itemKey = getItemKey(item);
                const isSelected = step === 1
                  ? getItemKey(selectedRole) === itemKey
                  : selectedAccessories.some((x) => getItemKey(x) === itemKey);

                return (
                  <div
                    key={itemKey}
                    className={`${Styles.gridItem} ${isSelected ? Styles.gridItemSelected : ''}`}
                    onClick={() => (step === 1 ? onSelectRole(item) : onToggleAccessory(item))}
                  >
                    <div className={Styles.itemImg}>
                      <img src={item.nftImage} alt={item.collectionName || item.nftNo} />
                      {item.ownedCount > 0 ? (
                        <div className={Styles.ownedBadge}>已拥有{item.ownedCount}款</div>
                      ) : null}
                      {isSelected ? <div className={Styles.selectedCorner} /> : null}
                    </div>
                    <div className={Styles.itemName}>{item.collectionName || item.nftNo}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className={Styles.bottomActionBar}>
        {step === 1 ? (
          <>
            <button type="button" className={Styles.nextStepBtn} onClick={onNext} />
            <button type="button" className={Styles.compoundBtn} onClick={onCompound} />
          </>
        ) : (
          <>
            <button type="button" className={Styles.lastStepBtn} onClick={onLast} />
            <button type="button" className={Styles.compoundBtn} onClick={onCompound} />
          </>
        )}
      </div>

      <NFTModal
        visible={confirmVisible}
        closeOnMaskClick={false}
        showCloseButton={false}
        onClose={() => setConfirmVisible(false)}
        bodyClassName={Styles.confirmModalWrap}
        bodyStyle={{
          background: 'transparent',
          padding: 0,
        }}
        content={
          <div className={`${Styles.confirmModal} ${selectedAccessories.length > 0 ? Styles.confirmModalStep2 : ''}`}>
            <div className={Styles.confirmTitle}>确认合成</div>
            <div className={Styles.confirmPreviewImg}>
              {selectedRole?.nftImage ? (
                <img className={Styles.confirmLayerImg} src={selectedRole.nftImage} alt="" />
              ) : null}
              {previewAccessoryLayers.map((item) => (
                <img
                  key={getItemKey(item)}
                  className={Styles.confirmLayerImg}
                  src={item.nftImage}
                  alt={item.collectionName || ''}
                />
              ))}
            </div>
            {selectedAccessories.length > 0 ? (
              <>
                <div className={Styles.selectedAccessoryLabel}>已选配件</div>
                <div className={Styles.previewSlots}>
                  {selectedAccessories.map((item) => (
                    <div key={getItemKey(item)} className={Styles.slot}>
                      {item?.nftImage ? <img src={item.nftImage} alt={item.collectionName} /> : null}
                    </div>
                  ))}
                </div>
                <div className={`${Styles.confirmWarning} ${Styles.confirmWarningWithSlots}`}>
                  <div>*合成后原角色</div>
                  <div>及原配件将被销毁且不可撤销</div>
                </div>
              </>
            ) : (
              <>
                <div className={Styles.confirmMessage}>您未选择配件，确认合成？</div>
                <div className={Styles.confirmWarning}>
                  <div>*合成后原角色</div>
                  <div>及原配件将被销毁且不可撤销</div>
                </div>
              </>
            )}
            <div className={`${Styles.confirmActions} ${selectedAccessories.length > 0 ? Styles.confirmActionsStep2 : ''}`}>
              <button type="button" className={Styles.cancelImgBtn} onClick={() => setConfirmVisible(false)} />
              <button type="button" className={Styles.confirmImgBtn} onClick={onConfirmCompound} />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default ExchangePage;
